/* eslint-disable no-undef */
import { FC } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { Form, Formik, FormikValues } from "formik";

import { ReactComponent as Image } from "../../../assets/image.svg";
import Button from "../../../custom/button/button";
import Input from "../../../custom/input/input";
import Select from "../../../custom/select/select";
import Upload from "../../../custom/upload/upload";
import {
  StatusOptions,
  createUpdateGeneralTemplate,
} from "../../../requests";
import { validateTemplate } from "../../../utils/validations";

interface Init {
  schoolName: string;
  logoUrl: string;
  homePage: any;
  aboutUs: any;
  loginBackground: any;
  email: string;
  address: string;
  logoImage: any;
  phoneNumber: string;
  status: any;
}

const fileValues: { label: string; name: keyof Init }[] = [
  {
    label: "Logo Image",
    name: "logoImage",
  },
  {
    label: "Home Page Image",
    name: "homePage",
  },
  {
    label: "About Us Image",
    name: "aboutUs",
  },
  {
    label: "Login Background Image",
    name: "loginBackground",
  },
];

interface EditTemplateProps {
  handleClose: () => void;
  data: GeneralTemplate;
}

const SetupSchoolInfoTemplate = ({
  handleClose,
}: {
  handleClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const { notification } = App.useApp();

  const createUpdateGeneralTemplateMutation = useMutation({
    mutationFn: createUpdateGeneralTemplate,
    mutationKey: ["create-update-general-template"],
  });

  const createUpdateGeneralTemplateHandler = async (
    values: FormikValues,
    resetForm: () => void
  ) => {
    try {
      const formData = new FormData();
      formData.append("SchoolName", values?.schoolName?.trim());
      formData.append("SchoolAddress", values?.address?.trim());
      formData.append("SchoolPhoneNumber", values?.phoneNumber?.trim());
      formData.append("SchoolEmailAddress", values?.email?.trim());
      formData.append("AboutUsImage", values?.aboutUs);
      formData.append("LoginBackgroundImage", values?.loginBackground);
      formData.append("Logo", values?.logoImage);
      formData.append("HomePageImage", values?.homePage);
      formData.append("ActiveStatus", values?.status);

      await createUpdateGeneralTemplateMutation.mutateAsync(formData, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          queryClient.refetchQueries({ queryKey: ["get-general-template"] });
          resetForm();
          handleClose();
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  return (
    <Formik
      initialValues={
        {
          schoolName: "",
          logoUrl: "",
          homePage: "",
          aboutUs: "",
          loginBackground: "",
          phoneNumber: "",
          email: "",
          address: "",
          status: "",
        } as Init
      }
      onSubmit={(values, { resetForm }) => {
        createUpdateGeneralTemplateHandler(values, resetForm);
      }}
      validationSchema={validateTemplate}
    >
      {({ values, setFieldValue }) => (
        <Form className="fields">
          <Input
            name="schoolName"
            label="School Name"
            placeholder="Input title"
          />
          <Input name="logoUrl" label="Logo Url" placeholder="Input url" />
          {fileValues.map((value) => (
            <>
              {values[value.name] ? (
                <div className="small-gap">
                  <Image />
                  <span>{`${values[value.name]?.name}`}</span>
                  <Button
                    onClick={() => setFieldValue(value.name, "")}
                    variant="text"
                    text="x"
                  />
                </div>
              ) : (
                <Upload
                  name={value.name}
                  label={value.label}
                  onChange={(e) => {
                    const file = e.target.files;
                    if (file) {
                      setFieldValue(value.name, file[0]);
                    }
                  }}
                />
              )}
            </>
          ))}
          <Input name="email" label="Email" placeholder="Input Email" />
          <Input
            name="phoneNumber"
            label="Phone Number"
            placeholder="Input Phone Number"
          />
          <Input
            name="address"
            label="Contact Address"
            placeholder="Input Address"
          />
          <Select
            name="status"
            placeholder="Select Status"
            label="Status"
            options={
              <>
                {StatusOptions.map((option: any) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </>
            }
          />
          <div className="btn-group">
            <Button onClick={handleClose} variant="text" text="Cancel" />
            <Button
              text="Create"
              disabled={createUpdateGeneralTemplateMutation?.isPending}
              isLoading={createUpdateGeneralTemplateMutation.isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

const EditTemplate: FC<EditTemplateProps> = ({ handleClose, data }) => {
  const queryClient = useQueryClient();
  const { notification } = App.useApp();

  const CreateUpdateGeneralTemplateMutation = useMutation({
    mutationFn: createUpdateGeneralTemplate,
    mutationKey: ["create-update-general-template"],
  });

  const CreateUpdateGeneralTemplateHandler = async (
    values: FormikValues,
    resetForm: () => void
  ) => {
    try {
      const formData = new FormData();
      formData.append("Id", data?.id?.toString());
      formData.append("SchoolName", values?.schoolName?.trim());
      formData.append("SchoolAddress", values?.address?.trim());
      formData.append("SchoolPhoneNumber", values?.phoneNumber?.trim());
      formData.append("SchoolEmailAddress", values?.email?.trim());
      formData.append(
        "AboutUsImage",
        values?.aboutUs === "" ? data?.aboutUsImageUrl : values?.aboutUs
      );
      formData.append("LoginBackgroundImage", values?.loginBackground);
      formData.append(
        "Logo",
        values?.logoImage === "" ? data?.logoUrl : values?.logo
      );
      formData.append(
        "HomePageImage",
        values?.homePage === "" ? data?.homePageImageUrl : values?.homePage
      );
      formData.append("ActiveStatus", values?.status);

      await CreateUpdateGeneralTemplateMutation.mutateAsync(formData, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          queryClient.refetchQueries({ queryKey: ["get-general-template"] });
          resetForm();
          handleClose();
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  return (
    <Formik
      initialValues={
        {
          schoolName: data?.schoolName,
          logoUrl: "",
          homePage: "",
          aboutUs: "",
          loginBackground: "",
          email: data?.schoolEmailAddress,
          address: data?.schoolAddress,
          status: data?.activeStatus,
          phoneNumber: data?.schoolPhoneNumber,
        } as Init
      }
      onSubmit={(values, { resetForm }) => {
        CreateUpdateGeneralTemplateHandler(values, resetForm);
      }}
    >
      {({ values, setFieldValue }) => (
        <Form className="fields">
          <Input
            name="schoolName"
            label="School Name"
            placeholder="Input title"
          />
          
          <Input name="logoUrl" label="Logo Url" placeholder="Input url" />

          {fileValues.map((value) => (
            <>
              {values[value.name] ? (
                <div className="small-gap">
                  <Image />
                  <span>{`${values[value.name]?.name}`}</span>
                  <Button
                    onClick={() => setFieldValue(value.name, "")}
                    variant="text"
                    text="x"
                  />
                </div>
              ) : (
                <Upload
                  name={value.name}
                  label={value.label}
                  onChange={(e) => {
                    const file = e.target.files;
                    if (file) {
                      setFieldValue(value.name, file[0]);
                    }
                  }}
                />
              )}
            </>
          ))}

          <Input name="email" label="Email" placeholder="Input Email" />

          <Input
            name="phoneNumber"
            label="Phone Number"
            placeholder="Input Phone Number"
          />

          <Input
            name="address"
            label="Contact Address"
            placeholder="Input Address"
          />

          <Select
            name="status"
            placeholder="Select Status"
            label="Status"
            options={
              <>
                {StatusOptions.map((option: any) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </>
            }
          />

          <div className="btn-group">
            <Button onClick={handleClose} variant="text" text="Cancel" />
            <Button
              text="Update"
              disabled={CreateUpdateGeneralTemplateMutation?.isPending}
              isLoading={CreateUpdateGeneralTemplateMutation.isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export { SetupSchoolInfoTemplate, EditTemplate };
